import React, { useCallback, useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useDeleteGoalBasedFinancialPlanGoal from "@fartherfinance/frontend/api/FinancialPlans/goals/hooks/useDeleteGoalBasedFinancialPlanGoal";
import usePatchGoalBasedFinancialPlanGoal from "@fartherfinance/frontend/api/FinancialPlans/goals/hooks/usePatchGoalBasedFinancialPlanGoal";
import usePostGoalBasedFinancialPlanGoal from "@fartherfinance/frontend/api/FinancialPlans/goals/hooks/usePostGoalBasedFinancialPlanGoal";
import type {
  GoalBasedFinancialPlanGoalResponse,
  GoalBasedGoalType,
} from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import {
  GoalBasedGoalTypeLabel,
  GoalBasedGoalTypeMap,
  GoalBasedGoalTypeSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import type { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { CreateUpdateFlowScrollContainerDivID } from "../Shared/constants";
import { convertAndFormatAgeToDateString } from "../Shared/helpers/convertAgeToDate";
import { getOwnerOption } from "../Shared/helpers/getOwnerOption";
import AddMoreSection from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/AddMoreSection/AddMoreSection";
import CardsRow from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/CardsRow/CardsRow";
import EmptyCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/EmptyCard/EmptyCard";
import FilledCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FilledCard/FilledCard";
import type {
  ClientAndSpouseDetails,
  ClientDetails,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import MenuOptionsWithCheckIfSelected from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import getEntries from "@src/utils/entries";
import { scrollElementIdToTopOfPage } from "@src/utils/scrollElementIdToTopOfPage";

import FilledGoalCard from "./FilledGoalCard/FilledGoalCard";
import AssetPurchaseFormDrawer from "./Forms/AssetPurchase";
import CarFormDrawer from "./Forms/Car";
import CashReserveFormDrawer from "./Forms/CashReserve";
import EducationFormDrawer from "./Forms/Education";
import GiftFormDrawer from "./Forms/Gift";
import HomeImprovementFormDrawer from "./Forms/HomeImprovement";
import HomePurchaseFormDrawer from "./Forms/HomePurchase";
import InvestmentPropertyFormDrawer from "./Forms/InvestmentProperty";
import LandFormDrawer from "./Forms/Land";
import LegacyFormDrawer from "./Forms/Legacy";
import OtherFormDrawer from "./Forms/Other";
import RetirementFormDrawer from "./Forms/Retirement";
import { GoalOwnerFieldType } from "./Forms/types";
import VacationFormDrawer from "./Forms/Vacation";
import VacationHomeFormDrawer from "./Forms/VacationHome";
import WeddingFormDrawer from "./Forms/Wedding";

import styles from "./Goals.module.css";

interface GoalOption {
  id: GoalBasedGoalType;
  label: GoalBasedGoalTypeLabel;
}

interface AddRetirementGoal extends GoalOption {
  owner: ClientDetails | undefined;
}

const goalOptionEntries = getEntries(GoalBasedGoalTypeMap);

const goalOptions = orderBy(
  goalOptionEntries.map(([key, value]) => ({
    label: value,
    id: key,
  })),
  [(g) => g.label],
  ["asc"]
);

interface Props {
  singleGoalBasedFinancialPlan: GoalBasedFinancialPlanSingleResponse;
  ownerOptions: GoalOwnerFieldType[];
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanGoals: React.FC<Props> = ({
  singleGoalBasedFinancialPlan,
  ownerOptions,
  clientSpouseData,
}) => {
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();
  const statusNotification = useStatusNotification();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [emptyCardOwner, setSelectedOwner] = useState<
    ClientDetails | undefined
  >(undefined);

  const [drawer, setDrawer] = useState<GoalBasedGoalType | undefined>(
    undefined
  );

  const [activeGoalToEdit, setActiveGoalToEdit] = useState<
    GoalBasedFinancialPlanGoalResponse | undefined
  >();

  const auth = useRequestAuth();

  const { client, spouse } = clientSpouseData;

  const { firstName: clientFirstName, fullName: clientFullName } = client;

  const spouseFirstName = spouse?.firstName ?? "Spouse";
  const spouseFullName = spouse?.fullName ?? "Spouse";

  const {
    mutateAsync: postGoalBasedFinancialPlanGoal,
    isLoading: isPostingGoalBasedFinancialPlanGoal,
  } = usePostGoalBasedFinancialPlanGoal(auth);

  const {
    mutateAsync: deleteGoalBasedFinancialPlanGoal,
    isLoading: isDeletingGoalBasedFinancialPlanGoal,
  } = useDeleteGoalBasedFinancialPlanGoal(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlanGoal,
    isLoading: isMutatingGoalBasedFinancialPlanGoal,
  } = usePatchGoalBasedFinancialPlanGoal(auth);

  const handleOnCloseDrawer = () => {
    setDrawer(undefined);
  };

  const handleOnRemove = async () => {
    if (activeGoalToEdit === undefined) {
      throw new Error("Goal to remove not found");
    }

    if (deleteGoalBasedFinancialPlanGoal === undefined) {
      throw new Error("deleteGoalBasedFinancialPlanGoal not found");
    }
    try {
      await deleteGoalBasedFinancialPlanGoal({
        goalBasedFinancialPlanId:
          singleGoalBasedFinancialPlan.goalBasedFinancialPlanId,
        goalBasedFinancialPlanGoalId: activeGoalToEdit.goalId,
      });

      statusNotification("Goal removed", "Success");

      setDrawer(undefined);
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        statusNotification(`Error removing goal: ${message ?? ""}`, "Error");
      } else {
        statusNotification(
          "There was an error removing the goal. Please try again.",
          "Error"
        );
      }
    }
  };

  const defaultOwner = useMemo(() => {
    const goalOwnerClientId = emptyCardOwner?.clientId;

    const ownerClientId =
      goalOwnerClientId !== undefined ? goalOwnerClientId : clientId;

    return getOwnerOption(ownerClientId, ownerOptions);
  }, [emptyCardOwner, clientId, ownerOptions]);

  const spouseHasRetirementHorizon =
    singleGoalBasedFinancialPlan.retirementHorizon.some(
      (r) => spouse?.clientId === r.owner
    );

  const commonDrawerProps = {
    goalBasedFinancialPlanId:
      singleGoalBasedFinancialPlan.goalBasedFinancialPlanId,
    activeGoalToEdit,
    defaultOwner,
    ownerOptions: ownerOptions,
    retirementHorizons: singleGoalBasedFinancialPlan.retirementHorizon,
    hideSpouseOptions: !spouseHasRetirementHorizon,
    onClose: handleOnCloseDrawer,
    onRemove: handleOnRemove,
    postGoalBasedFinancialPlanGoal,
    patchGoalBasedFinancialPlanGoal,
    deleteGoalBasedFinancialPlanGoal,
    isLoading:
      isMutatingGoalBasedFinancialPlanGoal ||
      isDeletingGoalBasedFinancialPlanGoal ||
      isPostingGoalBasedFinancialPlanGoal,
  };

  const handleOnSelectEmptyGoalOption =
    ({ id, owner }: AddRetirementGoal) =>
    () => {
      setSelectedOwner(owner);
      setActiveGoalToEdit(undefined);
      setDrawer(id);
      setShowDropdown(false);
    };

  const handleOnSelectGoalOption = ({ id }: GoalOption) => {
    setActiveGoalToEdit(undefined);
    setDrawer(id);
    setShowDropdown(false);
  };

  const handleOnSelectEditGoal =
    (goal: GoalBasedFinancialPlanGoalResponse) => () => {
      const owner = goal.owner !== clientId ? spouse : client;

      setSelectedOwner(owner);

      setActiveGoalToEdit(goal);
      setDrawer(goal.goalType);
      setShowDropdown(false);
    };

  const { goals } = singleGoalBasedFinancialPlan;
  const { Retirement } = GoalBasedGoalTypeSchema.Enum;

  const [clientRetirementGoal, spouseRetirementGoal] = useMemo(() => {
    const clientRetirementGoal = goals.find(
      (g) => g.goalType === Retirement && g.owner === clientId
    );
    const spouseRetirementGoal = goals.find(
      (g) => g.goalType === Retirement && g.owner === spouse?.clientId
    );
    return [clientRetirementGoal, spouseRetirementGoal];
  }, [goals, clientId, spouse, Retirement]);

  const getCardHeading = (goalType: GoalBasedGoalType) => {
    if (client.firstName === undefined) {
      return "";
    }

    const goalTypeLabel = goalOptions.find((t) => t.id === goalType)?.label;

    if (goalTypeLabel === undefined) {
      return "";
    }

    if (goalType === GoalBasedGoalTypeSchema.Enum.Retirement) {
      return `${client.firstName}'s Retirement Age`;
    }

    return goalTypeLabel;
  };

  const getNameOfClientOrSpouse = useCallback(
    (ownerId: ClientId) => {
      return ownerId === clientId ? clientFullName : spouseFullName;
    },
    [clientId, clientFullName, spouseFullName]
  );

  const spousePlaceholderData = {
    id: Retirement,
    label: Retirement,
    owner: spouse,
  };

  const clientPlaceholderData = {
    id: Retirement,
    label: Retirement,
    owner: client,
  };

  const showEmptySpouseCard =
    spouse !== undefined && spouseRetirementGoal === undefined;

  const addMoreGoalsText =
    goals.filter((g) => g.goalType !== Retirement).length === 0
      ? "Add other goals for education, cars, vacations, homes and more."
      : undefined;

  const onScrollToTopAndShowDropdown = () => {
    scrollElementIdToTopOfPage(CreateUpdateFlowScrollContainerDivID);
    setShowDropdown(true);
  };

  return (
    <>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Goals"
            subTitle="Add your goals including retirement age and big purchases like homes, education, and vacations."
          />
        }
        end={
          singleGoalBasedFinancialPlan.retirementHorizon.length === 0 ? (
            <Tooltip
              tooltipText={
                "You must add a target retirement age before adding more goals."
              }
              placement={"top"}
            >
              <div>
                <CustomDropdownBase
                  className={styles.dropdownBase}
                  curItem={{
                    value: "Add Goal",
                    displayComponent: (
                      <Text variant={"body"} color={"text"}>
                        Add Goal
                      </Text>
                    ),
                  }}
                  showDropdown={false}
                  setShowDropdown={() => undefined}
                  width={240}
                  isDisabled
                  hideIcon={true}
                />
              </div>
            </Tooltip>
          ) : (
            <CustomDropdownBase
              className={styles.dropdownBase}
              curItem={{
                value: "Add Goal",
                displayComponent: (
                  <Text variant={"body"} color={"text"}>
                    Add Goal
                  </Text>
                ),
              }}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              width={240}
              isDisabled={false}
              hideIcon={true}
            >
              <MenuOptionsWithCheckIfSelected
                onSelect={handleOnSelectGoalOption}
                curItemName={"Add Goal"}
                options={goalOptions.filter(({ id }) => id !== "Retirement")}
                maxHeight={515}
              />
            </CustomDropdownBase>
          )
        }
        endClassName={styles.pageHeroEnd}
      />

      <BodyContentFormatter>
        <Text variant={"heading1"} color={"text"} className={styles.heading}>
          Target Retirement Age
        </Text>

        <CardsRow maxColumns={2}>
          {clientRetirementGoal !== undefined ? (
            <FilledCard
              className={styles.filledCard}
              onClick={handleOnSelectEditGoal(clientRetirementGoal)}
              heading={`${clientFirstName}'s Retirement Age`}
              text={clientFullName}
              titleRight={convertAndFormatAgeToDateString({
                value: clientRetirementGoal?.startDate?.value,
                dateOfBirth: client?.dob,
                formatString: "yyyy",
              })}
              title={`${clientRetirementGoal.startDate?.value ?? ""}`}
              titleSubScript={"years old"}
            />
          ) : (
            <EmptyCard
              onAdd={handleOnSelectEmptyGoalOption(clientPlaceholderData)}
              heading={`${clientFirstName}'s Retirement Age`}
              text={clientFullName}
            />
          )}

          {spouseRetirementGoal !== undefined && spouse !== undefined && (
            <FilledCard
              className={styles.filledCard}
              onClick={handleOnSelectEditGoal(spouseRetirementGoal)}
              heading={`${spouseFirstName}'s Retirement Age`}
              text={spouseFullName}
              titleRight={convertAndFormatAgeToDateString({
                value: spouseRetirementGoal?.startDate?.value,
                dateOfBirth: spouse.dob,
                formatString: "yyyy",
              })}
              title={`${spouseRetirementGoal.startDate?.value ?? ""}`}
              titleSubScript={"years old"}
            />
          )}

          {showEmptySpouseCard && (
            <EmptyCard
              onAdd={handleOnSelectEmptyGoalOption(spousePlaceholderData)}
              heading={`${spouseFirstName}'s Retirement Age`}
              text={spouseFullName}
            />
          )}
        </CardsRow>

        <AddMoreSection
          heading={"Other Goals"}
          text={addMoreGoalsText}
          onClick={onScrollToTopAndShowDropdown}
        />

        <CardsRow maxColumns={2}>
          {orderBy(
            goals.filter((g) => g.goalType !== Retirement),
            [(g) => g.goalType],
            ["asc"]
          ).map((goal) => (
            <FilledGoalCard
              key={goal.goalId}
              heading={getCardHeading(goal.goalType)}
              endDate={goal?.endDate}
              financeMethod={goal.financeMethod}
              financing={goal.financing}
              startDate={goal?.startDate}
              clientAndSpouse={clientSpouseData}
              amount={goal.amount ?? ""}
              clientFullName={getNameOfClientOrSpouse(goal.owner)}
              onClick={handleOnSelectEditGoal(goal)}
              repeats={goal.repeats}
              goalType={goal.goalType}
              owner={goal.owner}
            />
          ))}
        </CardsRow>

        <AssetPurchaseFormDrawer
          drawerOpen={drawer === "AssetPurchase"}
          {...commonDrawerProps}
        />

        <CarFormDrawer
          drawerOpen={drawer === "Vehicle"}
          {...commonDrawerProps}
        />

        <CashReserveFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "CashReserve"}
        />

        <EducationFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "Education"}
        />

        <GiftFormDrawer drawerOpen={drawer === "Gift"} {...commonDrawerProps} />

        <HomeImprovementFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "HomeImprovement"}
        />

        <HomePurchaseFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "HomePurchase"}
        />

        <InvestmentPropertyFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "InvestmentProperty"}
        />

        <LandFormDrawer {...commonDrawerProps} drawerOpen={drawer === "Land"} />

        <LegacyFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "Legacy"}
        />

        <OtherFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "Other"}
        />

        <RetirementFormDrawer
          drawerOpen={drawer === "Retirement"}
          {...commonDrawerProps}
        />

        <VacationFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "Vacation"}
        />

        <VacationHomeFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "VacationHome"}
        />

        <WeddingFormDrawer
          {...commonDrawerProps}
          drawerOpen={drawer === "Wedding"}
        />
      </BodyContentFormatter>
    </>
  );
};

export default FinancialPlanGoals;
